import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DirectorLinks, ActorLinks } from "@/components/DirectorLinks";
import { CaretLeft, Eye, Clock, Warning, BookmarkSimple, SkipForward, SpotifyLogo, AppleLogo, LockKey, CornersOut, CornersIn, Moon, DeviceMobile, Play, FilmSlate, PictureInPicture } from "@phosphor-icons/react";
import { fetchVideo, fetchRelated, fetchVideos, fetchTrailer, formatDuration, formatViews, displayDuration } from "@/lib/api";
import { RedirectFallback } from "@/components/RedirectFallback";
import { VideoCard } from "@/components/VideoCard";
import { AdSlot } from "@/components/AdSlot";
import { CastHelpModal } from "@/components/CastHelpModal";
import { TvModeToggle } from "@/components/TvModeToggle";
import { UpNextOverlay } from "@/components/UpNextOverlay";
import { JustWatchWidget } from "@/components/JustWatchWidget";
import { ShareRow } from "@/components/ShareRow";
import { ScreenLock } from "@/components/ScreenLock";
import { SendToTvModal } from "@/components/SendToTvModal";
import { AmazonBuyButton } from "@/components/AmazonBuyButton";
import { useWatchLater } from "@/hooks/useWatchLater";
import { useVideoEnd, withJsApi, useAutoplayPref, getYtPlayer } from "@/hooks/useAutoplayNext";
import { useTvMode, isTvUserAgent } from "@/context/TvModeContext";
import { useMiniPlayer } from "@/context/MiniPlayerContext";
import { addToHistory } from "@/lib/watchHistory";
import { saveProgress, getProgress } from "@/lib/progress";
import { applyOg } from "@/lib/og";
import { kickRepaint } from "@/lib/repaint";

const IFRAME_ID = "rv-player-iframe";

export default function Player({ forceJustWatch = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: video, isLoading, isError } = useQuery({
    queryKey: ["video", id],
    queryFn: () => fetchVideo(id),
    retry: false,
  });

  const { data: related = [] } = useQuery({
    queryKey: ["related", id],
    queryFn: () => fetchRelated(id),
    enabled: !!video,
  });

  // Official trailer for the "Trailers & Extras" section — only for full AVOD movies
  // (discovery titles already play their trailer in the main player above).
  const { data: trailer } = useQuery({
    queryKey: ["trailer", id],
    queryFn: () => fetchTrailer(id),
    enabled: !!video && !video.is_discovery,
    staleTime: 1000 * 60 * 30,
  });
  const [trailerOpen, setTrailerOpen] = useState(false);
  useEffect(() => {
    if (!video) return;
    const origin = window.location.origin;
    const ref = searchParams.get("ref");
    return applyOg({
      title: `${video.title} · ReelVault`,
      description: `Watch ${video.title} free on ReelVault.`,
      image: `${origin}/api/og/${video.video_id}/image.jpg`,
      url: `${origin}/watch/${video.video_id}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`,
      type: "video.other",
    });
  }, [video, searchParams]);
  useEffect(() => setTrailerOpen(false), [id]);
  // Bumped when the user taps Watch Trailer / Play Movie — forces the main iframe to
  // remount with autoplay=1 so playback actually starts (postMessage alone is unreliable).
  const [playNonce, setPlayNonce] = useState(0);
  useEffect(() => setPlayNonce(0), [id]);

  // Always land at the very top when opening a movie detail page. Re-assert after the
  // video data arrives since async content (iframe, rows) can otherwise shift the scroll
  // position down below the fold.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id, video]);

  const { isSaved, toggle } = useWatchLater();
  const { tvMode } = useTvMode();
  const [autoplay, toggleAutoplay] = useAutoplayPref();
  const [showUpNext, setShowUpNext] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [napMode, setNapMode] = useState(false);
  const [showTvModal, setShowTvModal] = useState(false);
  const playerWrapRef = useRef(null);
  const positionRef = useRef(0);

  // Track fullscreen so the child-lock shield (a descendant of the wrapper) always
  // renders in the fullscreen top layer, covering the video.
  useEffect(() => {
    const onFs = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  const toggleFullscreen = () => {
    const el = playerWrapRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  };

  // CTA: scroll up to the player and start playback. Remounting the iframe with
  // autoplay=1 (via playNonce) reliably starts the trailer/movie on tap.
  const scrollToPlayerAndPlay = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
    playerWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPlayNonce((n) => n + 1);
    playingRef.current = true;
  };

  // In-app mini-player: keep a live snapshot so leaving the page while playing floats the video.
  const { openMini } = useMiniPlayer();
  const floatRef = useRef({ playing: false, time: 0, force: false });
  const videoRef = useRef(null);
  videoRef.current = video;
  useEffect(() => {
    const iv = setInterval(() => {
      const p = getYtPlayer(IFRAME_ID);
      if (!p?.getPlayerState) return;
      floatRef.current.playing = p.getPlayerState() === 1;
      floatRef.current.time = p.getCurrentTime?.() || floatRef.current.time;
    }, 1000);
    return () => {
      clearInterval(iv);
      const v = videoRef.current;
      const f = floatRef.current;
      const tv = document.documentElement.classList.contains("is-tv") || isTvUserAgent();
      if (v && !tv && (f.playing || f.force) && /youtube\.com\/embed/.test(v.monetized_embed_url || "")) {
        openMini({ video_id: v.video_id, title: v.title_override || v.title, thumb: v.default_thumb,
                   embedUrl: v.monetized_embed_url, duration: v.duration_sec, start: Math.floor(f.time), nonce: Date.now() });
      }
    };
  }, [id, openMini]);
  const minimize = () => {
    floatRef.current.force = true;
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/");
  };

  // Reset the Up Next prompt whenever we load a new title.
  useEffect(() => setShowUpNext(false), [id]);

  // Record this title as most-recently watched (powers the "Because you watched…" row).
  useEffect(() => {
    if (video) {
      addToHistory(video.video_id);
      localStorage.setItem("rv_watched_once", "1");
      window.dispatchEvent(new Event("rv-watched"));
    }
  }, [video]);

  // Keyboard: Space/MediaPlayPause play-pause everywhere; F, M, ←/→ (±10s) on desktop only.
  const playingRef = useRef(true);
  useEffect(() => {
    if (!video || !/youtube\.com\/embed/.test(video.monetized_embed_url || "")) return undefined;
    const send = (func, args = []) =>
      document.getElementById(IFRAME_ID)?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*");
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.ctrlKey || e.metaKey || e.altKey) return;
      const p = getYtPlayer(IFRAME_ID);
      if (e.code === "Space" && (tag === "BUTTON" || tag === "A")) return;
      if (e.key === "MediaPlayPause" || e.code === "Space") {
        e.preventDefault();
        const playing = p?.getPlayerState ? p.getPlayerState() === 1 : playingRef.current;
        send(playing ? "pauseVideo" : "playVideo");
        playingRef.current = !playing;
        return;
      }
      if (tvMode || document.documentElement.classList.contains("is-tv")) return;
      const k = e.key.toLowerCase();
      if (k === "f") {
        e.preventDefault();
        toggleFullscreen();
      } else if (k === "m") {
        e.preventDefault();
        send(p?.isMuted?.() ? "unMute" : "mute");
      } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && p?.getCurrentTime) {
        e.preventDefault();
        const dur = p.getDuration?.() || Infinity;
        const t = Math.min(dur, Math.max(0, p.getCurrentTime() + (e.key === "ArrowRight" ? 10 : -10)));
        send("seekTo", [t, true]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [video, id, tvMode]);

  // Fallback pool if there are no related videos.
  const { data: fallback } = useQuery({
    queryKey: ["player-fallback"],
    queryFn: () => fetchVideos({ page: 1, limit: 6, sort: "watched" }),
    enabled: !!video && related.length === 0,
  });

  // Kids safety: a kids title must only ever auto-advance to another KIDS title in the
  // same row — never general/trending content.
  const isKids = useMemo(
    () => (video?.keywords || []).map((k) => String(k).toLowerCase()).includes("kids"),
    [video]
  );
  const kidsRowTag = useMemo(() => {
    const kws = (video?.keywords || []).map((k) => String(k).toLowerCase());
    return ["numberblocks", "learn", "sing-along", "family"].find((t) => kws.includes(t)) || null;
  }, [video]);
  const { data: kidsPool } = useQuery({
    queryKey: ["kids-next", kidsRowTag, id],
    queryFn: () => fetchVideos({ page: 1, limit: 24, content_type: "kids", tag: kidsRowTag }),
    enabled: !!video && isKids && !!kidsRowTag,
  });

  // Next = related title with the closest release year (same genre/keyword overlap), else top related, else trending.
  const next = useMemo(() => {
    if (isKids) {
      const items = kidsPool?.items || [];
      const idx = items.findIndex((v) => v.video_id === id);
      if (idx >= 0 && items[idx + 1]) return items[idx + 1];
      return items.find((v) => v.video_id !== id) || null;
    }
    if (related && related.length) {
      if (video?.release_year) {
        return [...related].sort(
          (a, b) =>
            Math.abs((a.release_year || 0) - video.release_year) -
            Math.abs((b.release_year || 0) - video.release_year)
        )[0];
      }
      return related[0];
    }
    return (fallback?.items || []).find((v) => v.video_id !== id) || null;
  }, [isKids, kidsPool, related, video, fallback, id]);

  // Resume position: a handoff/deep-link ?t= wins, otherwise the saved localStorage progress.
  const resumeStart = useMemo(() => {
    const t = parseInt(searchParams.get("t") || "0", 10);
    if (t > 5) return t;
    const p = getProgress(id);
    return p && p.timestamp > 5 ? Math.floor(p.timestamp) : 0;
  }, [id, searchParams]);

  const trailerMissing = !!video?.is_discovery && !/youtube(?:-nocookie)?\.com\/embed\/[A-Za-z0-9_-]{11}/.test(video?.monetized_embed_url || "");

  const embedSrc = useMemo(() => {
    if (!video) return "";
    let url = withJsApi(video.monetized_embed_url);
    const isYt = /youtube\.com\/embed/.test(url);
    if (resumeStart > 0 && isYt) url += `&start=${resumeStart}`;
    // Auto-advance (countdown / Play Next / Play Now) OR a Watch Trailer / Play Movie tap
    // carries autoplay=1 so the title starts playing immediately instead of loading paused.
    if ((searchParams.get("autoplay") === "1" || playNonce > 0) && !/[?&]autoplay=1/.test(url)) {
      const sep = url.includes("?") ? "&" : "?";
      url += `${sep}autoplay=1`;
    }
    // Disable YouTube's native fullscreen so we own a fullscreen wrapper the child-lock can cover.
    if (isYt) url += "&fs=0";
    return url;
  }, [video, resumeStart, searchParams, playNonce]);

  useVideoEnd({
    videoId: id,
    embedUrl: video?.monetized_embed_url,
    durationSec: video?.duration_sec,
    iframeId: IFRAME_ID,
    enabled: !!video,
    onEnd: () => {
      if (autoplay && next && !napMode) setShowUpNext(true);
    },
    onProgress: (ct, du) => {
      positionRef.current = ct;
      saveProgress({ video_id: id, timestamp: ct, duration: du, title: video?.title, thumb: video?.default_thumb });
    },
  });

  // Nap Mode: cap the YouTube volume (~30%) via the iframe JS API; restore on exit.
  useEffect(() => {
    const iframe = document.getElementById(IFRAME_ID);
    if (!iframe?.contentWindow) return;
    const cmd = (func, args) =>
      iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func, args }), "*");
    cmd("setVolume", [napMode ? 30 : 100]);
  }, [napMode, video]);

  return (
    <div className="min-h-screen bg-[#050505]">
      {video && (
        <Helmet>
          <title>{`${video.title} · ReelVault`}</title>
          {forceJustWatch && <meta name="robots" content="noindex, nofollow" />}
          <meta name="description" content={`Watch ${video.title} on ReelVault — independent short films and public-domain cinema. ${formatViews(video.views)} views.`} />
        </Helmet>
      )}
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-4 flex items-center gap-4">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white/70 hover:text-amber transition-colors duration-300"
          >
            <CaretLeft size={16} /> Back
          </button>
          <div className="ml-auto flex items-center gap-3">
            <button
              data-testid="screen-lock-btn"
              onClick={() => setIsLocked(true)}
              title="Lock the screen so little hands can't pause or skip"
              className="flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] text-white/70 hover:border-sky-400 hover:text-sky-400 transition-colors duration-300"
            >
              <LockKey size={16} weight="duotone" /> <span className="hidden sm:inline">Lock</span>
            </button>
            <div className="hidden sm:block">
              <CastHelpModal />
            </div>
            <TvModeToggle />
            <Link to="/" className="flex items-center gap-2 group">
              <img src="/reelvault-mark.png" alt="ReelVault" data-testid="brand-logo" className="h-7 w-auto group-hover:scale-105 transition-transform duration-300" />
              <span className="font-serif text-xl tracking-tight text-[#EDEDED] hidden sm:block">
                Reel<span className="text-crimson">Vault</span>
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-7xl px-5 sm:px-8 py-8 rv-fade">
        {isError ? (
          <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="player-error">
            <Warning size={56} weight="light" className="text-crimson mb-6" />
            <p className="font-serif text-2xl text-white/80">Film unavailable</p>
            <p className="mt-2 text-white/40 text-sm">This title could not be found in the vault.</p>
            <Link to="/" className="mt-6 border border-white/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-amber hover:border-amber transition-colors duration-300">
              Back to films
            </Link>
          </div>
        ) : (
          <>
            {/* Top leaderboard ad */}
            <div className="flex justify-center mb-6">
              <AdSlot type="leaderboard" disabled={isKids} />
            </div>

            {/* Player */}
            <div ref={playerWrapRef} className="rv-player relative aspect-video w-full bg-black border border-white/10 overflow-hidden">
              {isLoading ? (
                <div className="h-full w-full animate-pulse bg-ink-surface" />
              ) : video.requires_affiliate_redirect && !isKids ? (
                <RedirectFallback video={video} />
              ) : trailerMissing ? (
                <div data-testid="trailer-unavailable" className="absolute inset-0">
                  {video.default_thumb && <img src={video.default_thumb} alt={video.title} className="h-full w-full object-cover opacity-50" />}
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-t from-black/90 via-black/40 to-black/20 text-center px-6">
                    <FilmSlate size={34} weight="duotone" className="text-amber" aria-hidden="true" />
                    <p className="font-serif text-2xl sm:text-3xl text-white">Trailer Currently Unavailable</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">See where to stream it below</p>
                  </div>
                </div>
              ) : (
                <iframe
                  key={`${id}-${playNonce}`}
                  data-testid="video-iframe"
                  id={IFRAME_ID}
                  src={embedSrc}
                  title={video.title}
                  tabIndex={0}
                  onLoad={(e) => kickRepaint(e.currentTarget)}
                  className="absolute inset-0 z-20 h-full w-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
              {!isLoading && !isLocked && (
                <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
                  <button
                    data-testid="send-to-tv-btn"
                    onClick={() => setShowTvModal(true)}
                    title="Send to TV — resume on another device"
                    className="flex items-center justify-center rounded-full bg-black/55 backdrop-blur-md border border-white/25 p-2 text-white/90 hover:bg-amber hover:text-black hover:border-amber transition-colors duration-300"
                  >
                    <DeviceMobile size={16} weight="duotone" />
                  </button>
                  {isKids && (
                    <button
                      data-testid="nap-mode-btn"
                      onClick={() => setNapMode((v) => !v)}
                      title="Nap Mode — dim screen, lower volume, stop autoplay"
                      className={`flex items-center gap-1.5 rounded-full backdrop-blur-md border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                        napMode
                          ? "bg-indigo-400 text-black border-indigo-400"
                          : "bg-black/55 text-white/90 border-white/25 hover:border-indigo-300 hover:text-indigo-300"
                      }`}
                    >
                      <Moon size={15} weight={napMode ? "fill" : "duotone"} /> Nap
                    </button>
                  )}
                  {!tvMode && !trailerMissing && !video.requires_affiliate_redirect && /youtube\.com\/embed/.test(video?.monetized_embed_url || "") && (
                    <button
                      data-testid="minimize-player-btn"
                      onClick={minimize}
                      title="Minimize — keep watching in a floating mini-player while you browse"
                      aria-label="Minimize to mini-player"
                      className="flex items-center justify-center rounded-full bg-black/55 backdrop-blur-md border border-white/25 p-2 text-white/90 hover:bg-amber hover:text-black hover:border-amber transition-colors duration-300"
                    >
                      <PictureInPicture size={16} weight="bold" />
                    </button>
                  )}
                  <button
                    data-testid="fullscreen-btn"
                    onClick={toggleFullscreen}
                    title={isFs ? "Exit fullscreen" : "Fullscreen"}
                    className="flex items-center justify-center rounded-full bg-black/55 backdrop-blur-md border border-white/25 p-2 text-white/90 hover:bg-white hover:text-black hover:border-white transition-colors duration-300"
                  >
                    {isFs ? <CornersIn size={16} weight="bold" /> : <CornersOut size={16} weight="bold" />}
                  </button>
                  <button
                    data-testid="screen-lock-btn-overlay"
                    onClick={() => setIsLocked(true)}
                    title="Lock the screen so little hands can't pause or skip"
                    className="flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/25 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-white/90 hover:bg-sky-400 hover:text-black hover:border-sky-400 transition-colors duration-300"
                  >
                    <LockKey size={15} weight="duotone" /> Lock
                  </button>
                </div>
              )}
              {isLocked && <ScreenLock onUnlock={() => setIsLocked(false)} />}
            </div>

            {/* Split layout: main content + sidebar */}
            <div className="pt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
              {/* Main column */}
              <div className="lg:col-span-2">
                {isLoading ? (
                  <div className="space-y-4 animate-pulse">
                    <div className="h-9 w-2/3 bg-ink-surface" />
                    <div className="h-4 w-1/3 bg-ink-surface" />
                  </div>
                ) : (
                  <>
                    <h1 data-testid="video-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#EDEDED] leading-tight">
                      {video.title}
                    </h1>
                    <DirectorLinks names={video.directors} testid="player-director-links" className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-amber/90" />
                    <ActorLinks names={video.cast} max={5} testid="player-actor-links" className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-amber/90" />

                    {video.is_discovery && (
                      <p data-testid="discovery-badge" className="mt-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                        <FilmSlate size={14} weight="duotone" className="text-crimson" aria-hidden="true" />
                        Trailer &amp; Streaming Guide
                      </p>
                    )}

                    {/* Conditional CTA — Watch Trailer (Hollywood discovery) vs Play Movie (free AVOD) */}
                    <div className="mt-5">
                      {!trailerMissing && <button
                        type="button"
                        data-testid={video.is_discovery ? "watch-trailer-btn" : "play-movie-btn"}
                        onClick={scrollToPlayerAndPlay}
                        className="group relative z-10 inline-flex cursor-pointer items-center gap-3 rounded-full bg-amber px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.18em] text-black font-semibold hover:bg-crimson hover:text-white transition-colors duration-300"
                      >
                        {video.is_discovery ? (
                          <>
                            <FilmSlate size={18} weight="fill" className="pointer-events-none" /> Watch Trailer
                          </>
                        ) : (
                          <>
                            <Play size={18} weight="fill" className="pointer-events-none" /> Play Movie
                          </>
                        )}
                      </button>}
                      {video.is_discovery && !trailerMissing && (
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
                          Official trailer · Full film via the streaming guide below
                        </p>
                      )}
                      {!isKids && <AmazonBuyButton videoId={video.video_id} title={video.title} />}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-6 font-mono text-xs uppercase tracking-[0.15em] text-white/50">
                      <span data-testid="video-views" className="flex items-center gap-2">
                        <Eye size={16} weight="duotone" className="text-amber" />
                        {formatViews(video.views)} views
                      </span>
                      {displayDuration(video) ? (
                        <span data-testid="video-duration" className="flex items-center gap-2">
                          <Clock size={16} weight="duotone" className="text-amber" />
                          {formatDuration(displayDuration(video))}
                          {video.is_discovery && <span className="text-white/35">trailer</span>}
                        </span>
                      ) : null}
                      {video.is_discovery && video.tmdb_runtime ? (
                        <span data-testid="feature-runtime" className="text-white/60">
                          Feature Runtime: {Math.floor(video.tmdb_runtime / 3600) ? `${Math.floor(video.tmdb_runtime / 3600)}h ` : ""}{Math.round((video.tmdb_runtime % 3600) / 60)}m
                        </span>
                      ) : null}
                      <button
                        data-testid="save-button"
                        onClick={() => toggle(video.video_id, { kids: isKids })}
                        className={`flex items-center gap-2 border px-4 py-2 transition-colors duration-300 ${
                          isSaved(video.video_id)
                            ? "border-amber bg-amber/10 text-amber"
                            : "border-white/15 text-white/70 hover:border-amber hover:text-amber"
                        }`}
                      >
                        <BookmarkSimple size={16} weight={isSaved(video.video_id) ? "fill" : "regular"} />
                        {isSaved(video.video_id) ? "Saved" : "Save for Later"}
                      </button>
                      <button
                        data-testid="autoplay-toggle"
                        onClick={toggleAutoplay}
                        aria-pressed={autoplay}
                        title="Autoplay the next related video when this one ends"
                        className={`flex items-center gap-2 border px-4 py-2 transition-colors duration-300 ${
                          autoplay
                            ? "border-amber bg-amber/10 text-amber"
                            : "border-white/15 text-white/70 hover:border-amber hover:text-amber"
                        }`}
                      >
                        <SkipForward size={16} weight={autoplay ? "fill" : "regular"} />
                        Autoplay {autoplay ? "On" : "Off"}
                      </button>
                      {next && (
                        <button
                          data-testid="play-next-btn"
                          onClick={() => navigate(`/watch/${next.video_id}?autoplay=1`)}
                          title={`Play next: ${next.title}`}
                          className="flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2 hover:border-amber hover:text-amber transition-colors duration-300"
                        >
                          <SkipForward size={16} weight="fill" /> Play Next
                        </button>
                      )}
                    </div>

                    <ShareRow title={video.title} videoId={video.video_id} />

                    <div className="mt-6 h-px w-full bg-white/10" />

                    {video.content_type === "music" && (
                      <section className="mt-6" data-testid="music-streaming">
                        <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Stream This Release</h3>
                        <div className="flex flex-wrap gap-3">
                          <a
                            href={`https://open.spotify.com/search/${encodeURIComponent(video.title)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="spotify-link"
                            className="flex items-center gap-2 border border-white/10 bg-[#0A0A0A] px-4 py-2.5 text-white/90 hover:border-[#1DB954] hover:text-[#1DB954] transition-colors duration-300"
                          >
                            <SpotifyLogo size={20} weight="fill" /> Stream on Spotify
                          </a>
                          <a
                            href={`https://music.apple.com/search?term=${encodeURIComponent(video.title)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="apple-music-link"
                            className="flex items-center gap-2 border border-white/10 bg-[#0A0A0A] px-4 py-2.5 text-white/90 hover:border-amber hover:text-amber transition-colors duration-300"
                          >
                            <AppleLogo size={20} weight="fill" /> Apple Music
                          </a>
                        </div>
                      </section>
                    )}

                    <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-3">
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/30 mr-2">Tags</span>
                      {video.keywords?.map((kw) => (
                        <Link
                          key={kw}
                          to={`/?q=${encodeURIComponent(kw)}`}
                          data-testid={`tag-${kw}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("/", { state: { keyword: kw } });
                          }}
                          className="text-xs uppercase tracking-wider font-mono text-amber hover:text-crimson cursor-pointer transition-colors duration-300 border border-white/10 hover:border-crimson px-3 py-1.5"
                        >
                          {kw}
                        </Link>
                      ))}
                    </div>

                    {(video.tmdb_overview || video.description) && (
                      <p data-testid="video-description" className="mt-6 text-white/60 text-sm leading-relaxed whitespace-pre-line max-w-2xl">
                        {video.tmdb_overview || (video.description.length > 600 ? `${video.description.slice(0, 600)}…` : video.description)}
                      </p>
                    )}

                    {/* Discovery titles → JustWatch widget (rent/stream). AVOD full movies
                        get no widget here (they're free + have Trailers & Extras below). */}
                    {(forceJustWatch || video.is_discovery) ? (
                      <JustWatchWidget title={video.title} year={video.release_year} videoId={video.video_id} isShow={/^tmdb-tv-/.test(video.video_id) || (video.keywords || []).includes("tv")} tmdbId={(video.video_id.match(/^tmdb-(?:tv-)?(\d+)$/) || [])[1] || video.tmdb_id} />
                    ) : null}

                    {/* Trailers & Extras — full AVOD movies only; the main player holds the film. */}
                    {!video.is_discovery && trailer?.trailer_url && (
                      <section data-testid="trailers-extras" className="mt-8">
                        <h3 className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 mb-4">
                          <FilmSlate size={16} weight="duotone" className="text-amber" />
                          Trailers & Extras
                        </h3>
                        {trailerOpen ? (
                          <div className="rv-player relative aspect-video w-full max-w-2xl bg-black border border-white/10 overflow-hidden rounded-lg">
                            <iframe
                              data-testid="extras-trailer-iframe"
                              src={`${trailer.trailer_url}?autoplay=1&rel=0`}
                              title={`${video.title} — Official Trailer`}
                              className="h-full w-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <button
                            data-testid="extras-trailer-thumb"
                            onClick={() => setTrailerOpen(true)}
                            className="group relative block aspect-video w-full max-w-sm overflow-hidden rounded-lg border border-white/10 bg-black"
                          >
                            <img
                              src={trailer.thumbnail}
                              alt={`${video.title} official trailer`}
                              className="h-full w-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                            />
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="flex items-center justify-center rounded-full bg-black/55 backdrop-blur-md border border-white/30 p-4 text-white group-hover:bg-amber group-hover:text-black group-hover:border-amber transition-colors duration-300">
                                <Play size={26} weight="fill" />
                              </span>
                            </span>
                            <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 text-left">
                              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/80">Official Trailer</span>
                            </span>
                          </button>
                        )}
                      </section>
                    )}
                  </>
                )}
              </div>

              {/* Sidebar column */}
              <aside className="lg:col-span-1 space-y-8">
                <div className="flex justify-center lg:justify-start">
                  <AdSlot type="rectangle" disabled={isKids} />
                </div>
              </aside>
            </div>

            {related.length > 0 && (
              <section data-testid="related-section" className="mt-12">
                <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-5">Related Movies</h2>
                <div className="rv-snap-row flex gap-4 overflow-x-auto pb-4 rv-scroll-x" data-testid="related-grid">
                  {related.map((v, i) => (
                    <div key={v.video_id} className="rv-row-card w-[240px] sm:w-[300px] shrink-0">
                      <VideoCard video={v} index={i} />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {showUpNext && next && (
          <UpNextOverlay next={next} tvMode={tvMode} onCancel={() => setShowUpNext(false)} />
        )}
      </main>

      {napMode && (
        <>
          <div data-testid="nap-dim" className="fixed inset-0 z-[80] bg-black/55 pointer-events-none" />
          <button
            data-testid="nap-mode-exit"
            onClick={() => setNapMode(false)}
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[85] flex items-center gap-2 rounded-full border border-indigo-300/50 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-indigo-300"
          >
            <Moon size={14} weight="fill" /> Nap Mode · volume 30% · autoplay off · tap to exit
          </button>
        </>
      )}

      {showTvModal && video && (
        <SendToTvModal videoId={video.video_id} position={positionRef.current} onClose={() => setShowTvModal(false)} />
      )}
    </div>
  );
}
